import type { Hip3Market, SmartRoute } from '../types';

interface OrderConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  isLoading: boolean;
  market: Hip3Market;
  side: 'buy' | 'sell';
  size: number;
  type: 'market' | 'limit';
  price?: number;
  leverage: number;
  smartRoute?: SmartRoute | null;
  currentPrice?: number;
}

export function OrderConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  isLoading,
  market,
  side,
  size,
  type,
  price,
  leverage,
  smartRoute,
  currentPrice,
}: OrderConfirmationModalProps) {
  if (!isOpen) return null;

  const executionPrice = type === 'limit' ? price : (smartRoute?.weightedAvgPrice || currentPrice);
  const orderValue = executionPrice ? size * executionPrice : 0;
  const marginRequired = leverage > 1 ? orderValue / leverage : orderValue;

  return (
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
      onClick={onClose}
    >
      <div
        className="bg-[#0e0f10] border border-[#2d3748] rounded-lg w-[420px] max-w-[90vw] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#2d3748]">
          <h3 className="text-white font-semibold text-[15px]">Confirm Order</h3>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-[#6c7884] hover:text-white transition-colors disabled:opacity-50"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-4">
          {/* Order Direction Badge */}
          <div className="flex items-center justify-between">
            <span className="text-[#6c7884] text-[13px]">Direction</span>
            <span
              className={`px-3 py-1 rounded text-[13px] font-semibold ${
                side === 'buy'
                  ? 'bg-[#0ecb81]/20 text-[#0ecb81]'
                  : 'bg-[#f6465d]/20 text-[#f6465d]'
              }`}
            >
              {side.toUpperCase()} {market.baseAsset}
            </span>
          </div>

          {/* Order Details */}
          <div className="space-y-3 text-[13px]">
            <div className="flex justify-between">
              <span className="text-[#6c7884]">Order Type</span>
              <span className="text-white font-medium capitalize">{type}</span>
            </div>

            <div className="flex justify-between">
              <span className="text-[#6c7884]">Size</span>
              <span className="text-white">{size.toLocaleString()} {market.baseAsset}</span>
            </div>

            {type === 'limit' && price && (
              <div className="flex justify-between">
                <span className="text-[#6c7884]">Limit Price</span>
                <span className="text-white">${price.toLocaleString()}</span>
              </div>
            )}

            {type === 'market' && executionPrice && (
              <div className="flex justify-between">
                <span className="text-[#6c7884]">Est. Price</span>
                <span className="text-white">${executionPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>
            )}

            <div className="flex justify-between">
              <span className="text-[#6c7884]">Leverage</span>
              <span className="text-white">{leverage}x</span>
            </div>

            <div className="border-t border-[#2d3748] pt-3 mt-3">
              <div className="flex justify-between">
                <span className="text-[#6c7884]">Order Value</span>
                <span className="text-white font-medium">
                  ${orderValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              </div>
            </div>

            <div className="flex justify-between">
              <span className="text-[#6c7884]">Margin Required</span>
              <span className="text-white">
                ${marginRequired.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>

            {/* Smart Route Info */}
            {smartRoute && smartRoute.splits.length > 1 && (
              <div className="border-t border-[#2d3748] pt-3 mt-3">
                <div className="flex justify-between mb-2">
                  <span className="text-[#6c7884]">Execution</span>
                  <span className="text-[#2962ff] text-[12px] font-semibold">{smartRoute.splits.length} venues</span>
                </div>
                <div className="bg-[#0b0c0d] rounded p-2 space-y-1">
                  {smartRoute.splits.map((split, idx) => (
                    <div key={idx} className="flex justify-between text-[12px]">
                      <span className="text-[#6c7884]">{split.provider}</span>
                      <span className="text-white">{split.percentage.toFixed(0)}% @ ${split.expectedPrice.toFixed(2)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Warning for market orders */}
          {type === 'market' && (
            <div className="bg-[#f6465d]/10 border border-[#f6465d]/30 rounded-md p-3">
              <p className="text-[#f6465d] text-[12px]">
                Market orders execute immediately at the best available price. Final execution price may differ from estimate.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-[#2d3748] flex gap-3">
          <button
            onClick={onClose}
            disabled={isLoading}
            className="flex-1 py-2.5 bg-[#1a1a1a] hover:bg-[#252525] text-white font-semibold text-[13px] rounded-md transition-all disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isLoading}
            className={`flex-1 py-2.5 font-semibold text-[13px] rounded-md transition-all disabled:opacity-50 ${
              side === 'buy'
                ? 'bg-[#0ecb81] hover:bg-[#0db872] text-white'
                : 'bg-[#f6465d] hover:bg-[#e03d52] text-white'
            }`}
          >
            {isLoading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Confirming...
              </span>
            ) : (
              `Confirm ${side === 'buy' ? 'Buy' : 'Sell'}`
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
